import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { validateCareer } from "../../utils/validation";
import {
  addCareer,
  getCareerById,
  updateCareer,
} from "../../services/Career/careerServices";
import { handleErrors } from "../../utils/errorHandler";
import { Loading } from "../Common/OtherElements/Loading";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";

const INITIAL_FORM = {
  role: "",
  url: "",
  location: "",
  shortDescription: "",
  fullDescription: "",
  pageTitle: "",
  metaDescription: "",
  metaKeywords: "",
};

const toJobUrl = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/\s+/g, "-");

export const AddCareer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const PageLevelAccessurl = "career";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isUrlEdited, setIsUrlEdited] = useState(false);

  useEffect(() => {
    if (!pageAccessData) return;

    if (id && !pageAccessData.editAccess) {
      navigate("/404-error-page");
    } else if (!id && !pageAccessData.addAccess) {
      navigate("/404-error-page");
    }
  }, [pageAccessData, id, navigate]);

  useEffect(() => {
    if (!id) return;

    const loadCareer = async () => {
      setLoading(true);
      try {
        const data = await getCareerById(id);
        setFormData({
          role: data.role || "",
          url: data.url || "",
          location: data.location || "",
          shortDescription: data.shortDescription || "",
          fullDescription: data.fullDescription || "",
          pageTitle: data.pageTitle || "",
          metaDescription: data.metaDescription || "",
          metaKeywords: data.metaKeywords || "",
        });
        setIsUrlEdited(true);
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    };

    loadCareer();
  }, [id]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "role" && !isUrlEdited) {
      setFormData((prev) => ({
        ...prev,
        role: value,
        url: toJobUrl(value),
      }));
    } else if (name === "url") {
      setIsUrlEdited(true);
      setFormData((prev) => ({ ...prev, url: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { valid, errors: validationErrors } = validateCareer(formData);
    setErrors(validationErrors);
    if (!valid) return;

    const payload = {
      role: formData.role.trim(),
      url: formData.url.trim(),
      location: formData.location.trim(),
      shortDescription: formData.shortDescription,
      fullDescription: formData.fullDescription,
      pageTitle: formData.pageTitle,
      metaDescription: formData.metaDescription,
      metaKeywords: formData.metaKeywords,
    };

    try {
      setIsButtonDisabled(true);
      if (id) {
        await updateCareer({ ...payload, id: Number(id) });
        navigate("/career", {
          state: { successMessage: "Job updated successfully!" },
        });
      } else {
        await addCareer(payload);
        navigate("/career", {
          state: { successMessage: "Job added successfully!" },
        });
      }
    } catch (error) {
      handleErrors(error);
      setIsButtonDisabled(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 job-form-title">{id ? "Update Job" : "Add Job"}</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="ri-home-2-fill"></i>
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/career">Manage Jobs</Link>
                </li>
                <li className="breadcrumb-item active">
                  {id ? "Update Job" : "Add Job"}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="job-form">
        <div className="row">
          <div className="col-lg-8">
            <div className="card mt-xxl-n5 p-3 job-form-card">
              <h5 className="blogs-heading">Job Details</h5>
              <div className="row mt-3">
                <div className="col-lg-6 mb-3">
                  <label className="form-label">
                    Role <span className="required-field">*</span>
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    placeholder="Enter role"
                    onChange={handleInputChange}
                    className={`form-control ${errors.role ? "is-invalid" : ""}`}
                  />
                  {errors.role && (
                    <div className="invalid-feedback">{errors.role}</div>
                  )}
                </div>
                <div className="col-lg-6 mb-3">
                  <label className="form-label">
                    URL <span className="required-field">*</span>
                  </label>
                  <input
                    type="text"
                    name="url"
                    value={formData.url}
                    placeholder="Enter URL"
                    onChange={handleInputChange}
                    className={`form-control ${errors.url ? "is-invalid" : ""}`}
                  />
                  {errors.url && (
                    <div className="invalid-feedback">{errors.url}</div>
                  )}
                </div>
                <div className="col-lg-12 mb-3">
                  <label className="form-label">
                    Location <span className="required-field">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    placeholder="Enter location"
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.location ? "is-invalid" : ""
                    }`}
                  />
                  {errors.location && (
                    <div className="invalid-feedback">{errors.location}</div>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Job Summary</label>
                <textarea
                  name="shortDescription"
                  value={formData.shortDescription}
                  placeholder="Enter job summary"
                  onChange={handleInputChange}
                  className="form-control"
                  rows="3"
                ></textarea>
              </div>
              <div className="mb-3">
                <label className="form-label">Full Description</label>
                <Editor
                  tinymceScriptSrc="/tinymce/tinymce.min.js"
                  value={formData.fullDescription}
                  init={{
                    height: 400,
                    menubar: false,
                    plugins: [
                      "advlist",
                      "autolink",
                      "link",
                      "lists",
                      "charmap",
                      "preview",
                      "anchor",
                      "searchreplace",
                      "wordcount",
                      "visualblocks",
                      "code",
                      "fullscreen",
                      "insertdatetime",
                      "table",
                      "help",
                    ],
                    toolbar:
                      "undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | code | link | preview fullscreen | help",
                  }}
                  onEditorChange={(value) =>
                    setFormData((prev) => ({ ...prev, fullDescription: value }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card mt-xxl-n5 p-3 job-form-card">
              <h5>Meta Data</h5>
              <div className="mt-3">
                <label className="form-label">Page Title</label>
                <input
                  type="text"
                  name="pageTitle"
                  value={formData.pageTitle}
                  placeholder="Enter page title"
                  onChange={handleInputChange}
                  className="form-control"
                />
              </div>
              <div className="mt-3">
                <label className="form-label">Meta Description</label>
                <textarea
                  name="metaDescription"
                  value={formData.metaDescription}
                  placeholder="Enter meta description"
                  onChange={handleInputChange}
                  className="form-control"
                  rows="3"
                ></textarea>
              </div>
              <div className="mt-3">
                <label className="form-label">Meta Keywords</label>
                <input
                  type="text"
                  name="metaKeywords"
                  value={formData.metaKeywords}
                  placeholder="Enter meta keywords"
                  onChange={handleInputChange}
                  className="form-control"
                />
              </div>
              <div className="mt-4">
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={isButtonDisabled}
                >
                  {isButtonDisabled
                    ? "Saving..."
                    : id
                    ? "Update Job"
                    : "Save Job"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
